import React from 'react';
import { useStyletron } from 'baseui';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'baseui/modal';
import { Button } from 'baseui/button';
import { Notification, KIND } from 'baseui/notification';

interface Change {
	path: string;
	original: string;
	edited: string;
}

interface DiffModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: () => void;
	saving: boolean;
	saveError: string | null;
	changes: Change[];
	config: any;
	editedConfig: any;
	hasChanges: () => boolean;
}

const DiffModal: React.FC<DiffModalProps> = ({
	isOpen,
	onClose,
	onSave,
	saving,
	saveError,
	changes,
	config,
	editedConfig,
	hasChanges
}) => {
	const [css] = useStyletron();

	return (
		<Modal isOpen={isOpen} onClose={onClose} size='default'>
			<ModalHeader>Review Changes</ModalHeader>
			<ModalBody>
				{/* Debug logging when modal opens */}
				{isOpen &&
					(() => {
						console.log('=== DEBUG: Show Diff ===');
						console.log('Original config:', config);
						console.log('Edited config:', editedConfig);
						console.log('Has changes:', hasChanges());
						console.log('Changes detected:', changes);
						return null;
					})()}
				{saveError && (
					<Notification kind={KIND.negative} overrides={{ Body: { style: { marginBottom: '16px' } } }}>
						{saveError}
					</Notification>
				)}

				<div className={css({ marginBottom: '16px' })}>
					<strong>The following changes will be applied:</strong>
				</div>

				{changes.length === 0 ? (
					<div className={css({ padding: '16px', textAlign: 'center', color: '#666' })}>No changes detected</div>
				) : (
					<div className={css({ maxHeight: '400px', overflowY: 'auto' })}>
						{changes.map((change) => (
							<div
								key={`change-${change.path}-${change.original}-${change.edited}`}
								className={css({
									marginBottom: '12px',
									padding: '12px',
									border: '1px solid #e0e0e0',
									borderRadius: '4px',
									backgroundColor: '#f9f9f9'
								})}>
								<div className={css({ fontWeight: 'bold', marginBottom: '4px' })}>{change.path}</div>
								<div className={css({ display: 'flex', gap: '8px', alignItems: 'center' })}>
									<div
										className={css({
											padding: '4px 8px',
											backgroundColor: '#ffebee',
											borderRadius: '4px',
											flex: 1
										})}>
										<strong>From:</strong> {String(change.original)}
									</div>
									<span>→</span>
									<div
										className={css({
											padding: '4px 8px',
											backgroundColor: '#e8f5e8',
											borderRadius: '4px',
											flex: 1
										})}>
										<strong>To:</strong> {String(change.edited)}
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				<div className={css({ display: 'flex', gap: '8px', justifyContent: 'flex-end' })}>
					<Button onClick={onClose} kind='secondary' disabled={saving}>
						Cancel
					</Button>
					<Button onClick={onSave} disabled={saving || changes.length === 0}>
						{saving ? 'Saving...' : 'Confirm & Save'}
					</Button>
				</div>
			</ModalFooter>
		</Modal>
	);
};

export default DiffModal;
